import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit'
import * as R from 'ramda'

import { getLastViewedSection } from 'components/Editor/EditorElements/elements/Button/helper'
import { getCanvasCommentName } from 'components/comments/constants'
import type { ScrollContext, ScrollType } from 'services/Scroll/ScrollService'
import ScrollService from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'

import { isAction } from '../actionTypeGuard'
import { setAnchor } from '../actions'
import { PreviewMode } from '../enums'
import {
  getBlocksState,
  getContinueBlockId,
  getScrollContext,
  getScrollReady,
  getSections,
} from '../selectors'
import type { IProjectContext } from '../types'

const TIMEOUT = 5000
const TICK = 16
const NODE_SCROLL_MARGIN = 50

const waitScroll = (
  store: MiddlewareAPI,
  scroll: ScrollType | ScrollType[],
  scrollContext: ScrollContext,
) => {
  if (Array.isArray(scroll)) {
    scroll.forEach((s) => waitScroll(store, s, scrollContext))
  } else {
    ScrollService.scroll(
      {
        ...scroll,
        complete: () => {
          let time = 0
          const interval = setInterval(() => {
            ScrollService[scroll.container]?.os?.scrollStop()
            ScrollService.scroll({ ...scroll, duration: 0 }, scrollContext)

            time += TICK
            const context: IProjectContext = store.getState().project
            const doScroll = getScrollReady(context)
            if (doScroll || time > TIMEOUT) {
              clearInterval(interval)
            }
          }, TICK)
        },
      },
      scrollContext,
    )
  }
}

export const scroll: Middleware = (store) => (next) => (action) => {
  const prevState = store.getState().project as IProjectContext
  const result = next(action)
  const nextState = store.getState().project as IProjectContext
  const scrollContext = getScrollContext(nextState)

  if (isAction(action, 'scroll')) {
    waitScroll(store, action.payload, scrollContext)
  }

  if (isAction(action, 'dragMove')) {
    const dnd = nextState.state.editor.dnd
    const currentY = dnd?.currentCoords?.y || 0
    const currentX = dnd?.currentCoords?.x || 0
    const zone = dnd?.zone
    const scroll =
      zone === 'canvas'
        ? ScrollService.canvas
        : zone === 'navigation'
          ? ScrollService.nodesNav
          : null

    if (scroll) {
      const anchorY = dnd?.anchorY ?? dnd?.initialCoords?.y ?? 0
      const anchorX = dnd?.anchorX ?? dnd?.initialCoords?.x ?? 0
      const viewport = scroll?.ref?.getBoundingClientRect() || { width: 1, height: 1, y: 1, x: 1 }
      const safeZoneY = viewport.height / 4
      const safeZoneX = viewport.width / 4

      if (dnd && Math.abs(anchorY - currentY) > safeZoneY) {
        store.dispatch(setAnchor({ y: viewport.y + viewport.height / 2 }))
      }
      if (dnd && Math.abs(anchorX - currentX) > safeZoneX) {
        store.dispatch(setAnchor({ x: viewport.x + viewport.width / 2 }))
      }
      const speedY = Math.abs(Math.abs(currentY - anchorY) - safeZoneY)
      const speedX = Math.abs(Math.abs(currentX - anchorX) - safeZoneX)

      scroll?.os?.scrollStop().scroll(
        {
          y:
            currentY < anchorY - safeZoneY
              ? `-=${speedY}`
              : currentY > anchorY + safeZoneY
                ? `+=${speedY}`
                : '+=0',
          x:
            currentX < anchorX - safeZoneX
              ? `-=${speedX}`
              : currentX > anchorX + safeZoneX
                ? `+=${speedX}`
                : '+=0',
        },
        100,
        'linear',
      )
    }
  }

  if (isAction(action, 'setDeviceMode')) {
    if (nextState.mode === PreviewMode.editor) {
      ScrollService.scroll(
        {
          container: ScrollContainerEnum.canvas,
          block: 'begin',
          scroll: ['never', 'always'],
          duration: 0,
          id: nextState.urlParams.blockId,
        },
        scrollContext,
      )
    }
  }

  if (isAction(action, 'setAppMode')) {
    waitScroll(
      store,
      {
        container: ScrollContainerEnum.canvas,
        id: nextState.urlParams.blockId,
        block: 'begin',
        scroll: ['never', 'ifneeded'],
        intersection: true,
      },
      scrollContext,
    )
  }

  if (isAction(action, 'setProjectNavigation')) {
    const { blockId, nodeIds, scroll } = action.payload
    const nodeId = nodeIds?.length === 1 ? nodeIds[0] : null
    if (!R.equals(prevState.urlParams.sectionId, nextState.urlParams.sectionId)) {
      ScrollService.scroll(
        { container: ScrollContainerEnum.canvas, y: 0, duration: 0 },
        scrollContext,
      )
    }

    if (scroll) {
      waitScroll(store, scroll, scrollContext)
    } else {
      waitScroll(
        store,
        {
          container: ScrollContainerEnum.blocksNav,
          id: blockId,
          block: 'nearest',
          scroll: 'ifneeded',
          intersection: true,
        },
        scrollContext,
      )
      waitScroll(
        store,
        {
          container: ScrollContainerEnum.nodesNav,
          id: nodeId,
          block: ['end', 'nearest'],
        },
        scrollContext,
      )

      if (nodeId) {
        waitScroll(
          store,
          {
            container: ScrollContainerEnum.canvas,
            id: nodeId,
            margin: NODE_SCROLL_MARGIN,
          },
          scrollContext,
        )
      } else if (blockId) {
        waitScroll(
          store,
          {
            container: ScrollContainerEnum.canvas,
            id: blockId,
            margin: NODE_SCROLL_MARGIN,
          },
          scrollContext,
        )
      }
    }
  }

  if (isAction(action, 'setOpenedComment') && action.payload) {
    const { blockId, threadId } = action.payload

    waitScroll(
      store,
      [
        {
          container: ScrollContainerEnum.canvas,
          id: threadId,
          block: 'center',
          scroll: ['never', 'ifneeded'],
        },
        {
          container: ScrollContainerEnum.blocksNav,
          id: blockId,
          block: 'nearest',
          scroll: 'ifneeded',
        },
        {
          container: ScrollContainerEnum.toolbar,
          id: threadId,
          block: 'nearest',
          scroll: 'ifneeded',
          intersection: true,
        },
      ],
      scrollContext,
    )
  }

  if (isAction(action, 'setElementNavigation') && action.payload) {
    const { nodeId } = action.payload
    waitScroll(
      store,
      [
        {
          container: ScrollContainerEnum.canvas,
          id: nodeId,
          block: 'center',
          scroll: 'ifneeded',
          intersection: true,
        },
      ],
      scrollContext,
    )
  }

  if (isAction(action, 'setOpenedTask') && action.payload) {
    const { blockId, taskId } = action.payload
    waitScroll(
      store,
      [
        {
          container: ScrollContainerEnum.canvas,
          id: blockId,
          block: 'begin',
          scroll: ['never', 'always'],
        },
        {
          container: ScrollContainerEnum.blocksNav,
          id: blockId,
          block: 'nearest',
          scroll: 'ifneeded',
        },
        {
          container: ScrollContainerEnum.toolbar,
          id: taskId,
          block: 'nearest',
          scroll: 'ifneeded',
        },
      ],
      scrollContext,
    )
  }

  if (isAction(action, 'setCommentForm') && action.payload) {
    const { blockId, elementId } = action.payload
    if (blockId) {
      waitScroll(
        store,
        [
          {
            container: ScrollContainerEnum.canvas,
            id: getCanvasCommentName(blockId, elementId),
            scroll: 'ifneeded',
            block: 'nearest',
            intersection: true,
          },
          {
            container: ScrollContainerEnum.blocksNav,
            id: blockId,
            block: 'nearest',
            scroll: 'ifneeded',
          },
        ],
        scrollContext,
      )
    }
  }

  if (
    isAction(action, 'setCourseNavigation') ||
    isAction(action, 'courseStart') ||
    isAction(action, 'testStart') ||
    isAction(action, 'testRestart') ||
    isAction(action, 'testEnd')
  ) {
    if (prevState.urlParams.sectionId !== nextState.urlParams.sectionId) {
      ScrollService.scroll(
        { container: ScrollContainerEnum.canvas, y: 0, duration: 0 },
        scrollContext,
      )
    }

    if (isAction(action, 'setCourseNavigation')) {
      waitScroll(
        store,
        {
          container: ScrollContainerEnum.sectionNav,
          id: action.payload.sectionId,
        },
        scrollContext,
      )
    }

    if (isAction(action, 'setCourseNavigation') && action.payload.scrollBlockId) {
      waitScroll(
        store,
        {
          container: ScrollContainerEnum.canvas,
          id: action.payload.scrollBlockId,
          block: 'begin',
          scroll: 'always',
        },
        scrollContext,
      )
    }
  }

  if (isAction(action, 'courseContinue') || isAction(action, 'init')) {
    const sections = getSections(nextState)
    const blocksState = getBlocksState(nextState)
    const lastViewedSection = getLastViewedSection(sections, blocksState)
    const continueBlock = getContinueBlockId(nextState, lastViewedSection?.id)
    if (continueBlock && nextState.mode === PreviewMode.scorm) {
      waitScroll(
        store,
        {
          container: ScrollContainerEnum.canvas,
          id: continueBlock,
          block: 'begin',
          scroll: 'always',
        },
        scrollContext,
      )
    }
  }
  if (isAction(action, 'init')) {
    const continueBlock = getContinueBlockId(nextState, nextState.urlParams.sectionId)
    if (continueBlock && nextState.mode === PreviewMode.scorm) {
      waitScroll(
        store,
        {
          container: ScrollContainerEnum.canvas,
          id: continueBlock,
          block: 'begin',
          scroll: 'always',
        },
        scrollContext,
      )
    }
  }
  if (isAction(action, 'continueBlock')) {
    ScrollService.scroll({ container: ScrollContainerEnum.canvas, y: '+=0.5vh' }, scrollContext)
  }
  if (isAction(action, 'continueAndHideBlock')) {
    ScrollService.scroll({ container: ScrollContainerEnum.canvas, y: '+=0.5vh' }, scrollContext)
  }

  return result
}
